import Service, { inject as service } from '@ember/service'
import { IN_REACT_NATIVE } from 'sh-www/constants/environment';

import config from 'sh-www/config/environment';

const logEvent = config.NODE_ENV !== 'production';
const measurementIds = config.GOOGLE_TAG_MANAGER_ID;
const gaEnabled = !IN_REACT_NATIVE & measurementIds.length > 0;

/**
 * @param {string} str
 * @returns {string}
 */
const appendLeadingAndTrailingSlashes = (str) => {
  let slashString = str;
  if (!slashString.startsWith('/')) slashString = `/${slashString}`;
  if (!slashString.endsWith('/')) slashString = `${slashString}/`;
  return slashString;
};

/**
 * @param {RouteInfo} routeInfo
 * @returns {string[]}
 */
const buildModelsFromRouteInfo = (routeInfo) => {
  const models = [];

  while (routeInfo) {
    models.unshift(routeInfo.paramNames.map((name) => routeInfo.params[name]));
    routeInfo = routeInfo.parent;
  }

  return models.flat();
};

class AnalyticsService extends Service {
  #initialized = false;

  @service
  router;

  /**
   * @param {Transition} transition
   * @param {string} title
   * @param {Function=} fn
   */
  trackPageViewFromTransition({ to }, title, fn) {
    const path = this.router.urlFor(to.name, ...buildModelsFromRouteInfo(to), to);
    const url = new URL(path, window.location.origin);

    fn?.(url);

    const page_title = appendLeadingAndTrailingSlashes(title || url.pathname);
    const page_location = url.href;

    if (gaEnabled) {
      gtag('set', { page_title, page_location });

      if (!this.#initialized) {
        gtag('js', new Date());
        measurementIds.forEach((id) => gtag('config', id, { send_page_view: false }));
        this.#initialized = true;
      }

      gtag('event', 'page_view');
    }

    if (logEvent) {
      console.log('page view:', { page_title, page_location });
    }
  }

  /**
   * @param {'click'|'generate_lead'|'sign_up'|'login'|'form_submit'} action
   * @param {Object=} opt_args
   */
  trackEvent(action, opt_args) {
    if (gaEnabled) {
      gtag('event', action, opt_args);
    } else if (opt_args?.event_callback) {
      setTimeout(opt_args.event_callback, 0);
    }

    if (logEvent) {
      console.log('event', { action, ...opt_args });
    }
  }

  /**
   * @param {'click'|'generate_lead'|'sign_up'|'login'|'form_submit'} action
   * @param {Object=} opt_args
   * @param {number=} opt_timeout
   * @return {Promise}
   */
  trackEventAsync(action, opt_args, opt_timeout = 2000) {
    return new Promise((resolve) => {
      let called = false;

      const event_callback = () => {
        if (!called) {
          called = true;
          resolve();
        }
      };

      setTimeout(event_callback, opt_timeout);
      this.trackEvent(action, { ...opt_args, event_callback });
    });
  }

  /**
   * @param {string|number} user_id
   */
  setUserId(user_id) {
    if (gaEnabled) {
      gtag('set', { user_id });
    }
  }
}

export default AnalyticsService;
